import { Dispatch, ReactElement, SetStateAction, useEffect } from "react";
import { useForm } from "react-hook-form";

import { ApiParameter, ApiParameterType } from "../api";
import { Button } from "./catalyst/Button.tsx";
import { Dialog } from "./catalyst/Dialog.tsx";
import { DialogActions } from "./catalyst/DialogActions.tsx";
import { DialogBody } from "./catalyst/DialogBody.tsx";
import { DialogTitle } from "./catalyst/DialogTitle.tsx";
import { ErrorMessage } from "./catalyst/ErrorMessage.tsx";
import { Field } from "./catalyst/Field.tsx";
import { FieldGroup } from "./catalyst/FieldGroup.tsx";
import { Fieldset } from "./catalyst/Fieldset.tsx";
import { Label } from "./catalyst/Label.tsx";
import { Legend } from "./catalyst/Legend.tsx";
import { Textarea } from "./catalyst/Textarea.tsx";
import InputFieldGroup from "./inputs/InputFieldGroup.tsx";

type APIParameterAddDialogProps = {
  currentParameterType: ApiParameterType;
  editingParameter?: ApiParameter | null;
  isDialogOpen: boolean;
  saveParameter: (data: ApiParameter) => void;
  setIsDialogOpen: Dispatch<SetStateAction<boolean>>;
};

const APIParameterAddDialog = ({
  currentParameterType,
  editingParameter = null,
  isDialogOpen,
  saveParameter,
  setIsDialogOpen,
}: APIParameterAddDialogProps): ReactElement => {
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<ApiParameter>({
    defaultValues: {
      description: "",
      name: "",
      type: currentParameterType,
      value: "",
    },
  });

  useEffect(() => {
    if (isDialogOpen) {
      reset(
        editingParameter || {
          description: "",
          name: "",
          type: currentParameterType,
          value: "",
        },
      );
    }
  }, [editingParameter, isDialogOpen, reset, currentParameterType]);

  const onSubmit = handleSubmit((data) => {
    saveParameter(data);
    setIsDialogOpen(false);
    reset({ description: "", name: "", type: currentParameterType, value: "" }); // Clear after save
  });

  return (
    <Dialog onClose={() => setIsDialogOpen(false)} open={isDialogOpen}>
      <DialogTitle>
        {editingParameter
          ? `Edit ${currentParameterType}`
          : `Add ${currentParameterType}`}
      </DialogTitle>
      <DialogBody>
        <Fieldset>
          <Legend>Parameter Definition</Legend>
          <FieldGroup>
            <InputFieldGroup<ApiParameter>
              errors={errors}
              fieldName="name"
              label="Name"
              placeHolder="Enter parameter name"
              register={register}
              rules={{ required: "Name is required" }}
            />
            <InputFieldGroup<ApiParameter>
              errors={errors}
              fieldName="value"
              label="Value"
              placeHolder="Enter parameter value"
              register={register}
              rules={{ required: "Value is required" }}
            />
            <Field>
              <Label>Description</Label>
              <Textarea
                {...register("description")}
                placeholder="Enter param value"
              />
              {errors.description && (
                <ErrorMessage>{errors.description.message}</ErrorMessage>
              )}
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button onClick={() => setIsDialogOpen(false)} plain>
          Cancel
        </Button>
        <Button color="emerald" onClick={onSubmit}>
          {editingParameter ? "Save" : "Add"} {currentParameterType}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default APIParameterAddDialog;
